import { ButtonVariant } from '@sprd/sprd-component-kit/v2';
import { CampaignCardProps } from '@/components/CampaignCard/CampaignCard';
import { Locale } from '@/utility/locale';

export interface Meta<T extends string> {
  name: string;
  deliveryKey?: string;
  deliveryId: string;
  schema: T;
  hierarchy?: {
    parentId: string;
    root: boolean;
  };
}

export interface PageMeta {
  title: string;
  description: string;
  keywords: string[];
  robots: string;
  pageType: 'MP' | 'CYO' | 'PRO';
  canonicalUrl?: string;
}

export interface HeaderFooterMeta extends Meta<'https://spreadshirt.com/site'> {
  title: string;
  description: string;
  keywords: string[];
  robots: string;
}

export type ContentItem =
  | FaqModule
  | TeaserRowModule
  | UspModule
  | HeaderImageModule
  | HeroTeaserModule
  | TeaserCampaignModule
  | TeaserCampaignMarkdownModule
  | TeaserCampaignModuleSlider
  | RecommendationTagsModule
  | CampaignSliderProps
  | CampaignSliderWithFiltersProps
  | SEOContentModule
  | ProductTypeSliderModuleContent
  | ProductSliderModuleContent
  | MarkdownElementContent
  | ReviewsSliderContent
  | TermsAndConditionsModule
  | AboutSpreadGroupModule
  | BreadcrumbsModule
  | InPageNavigationModule
  | AboutSpreadshirtModule
  | NewsletterModuleContent
  | LicensingListModuleContent
  | ProductSliderWithFiltersContent
  | GiftVoucherContent
  | RedemptionRulesContent
  | ChipFilterSliderContent
  | OnPageDiscountModule
  | TShirtOfTheWeekDiscountModule
  | VolumeDiscountCalculatorModuleContent;

export interface PageContent {
  content: {
    _meta: Meta<'https://spreadshirt.com/site/page'>;
    meta: PageMeta;
    contents: ContentItem[];
    breadcrumbLabel: string;
  };
}

export interface PreviewContent {
  _meta: Meta<'https://spreadshirt.com/site/page'>;
  meta: PageMeta;
  contents: ContentItem[];
}

export interface HomePageContent {
  content: {
    _meta: Meta<'https://spreadshirt.com/slots/homepage'>;
    meta: PageMeta;
    homepage: {
      _meta: Meta<'https://spreadshirt.com/site/homepage'>;
      meta: PageMeta;
      contents: ContentItem[];
    };
  };
}

export interface BreadcrumbData {
  name: string;
  slug: string;
}
export interface PageData {
  content: PageContent | HomePageContent;
  alternates?: { locale: Locale; slug: string }[] | undefined;
}

export type IconList = {
  [key: string]: React.ReactNode;
};

interface CmsModule<T extends string> {
  _meta: Meta<T>;
}

export interface UspModuleItem {
  heading: string;
  icon: string;
  description?: string;
  link?: AmplienceLinkType;
}

export interface UspModule extends CmsModule<'https://spreadshirt.com/modules/usp-bar'> {
  anchorId?: string;
  anchorLabel?: string;
  'usp-items': UspModuleItem[];
}

export interface FaqModule extends CmsModule<'https://spreadshirt.com/modules/faqs'> {
  anchorId?: string;
  anchorLabel?: string;
  headline: string;
  faqQuestionsAndAnswers: {
    faqQuestion: string;
    faqAnswer: string;
  }[];
}

export interface AmplienceImage {
  image: {
    name: string;
    endpoint: string;
    defaultHost: string;
    metaData?: {
      width: number;
      height: number;
    };
  };
  altText?: string;
}

export interface AmplienceVideo {
  id: string;
  name: string;
  endpoint: string;
  defaultHost: string;
  mimeType: string;
}

export interface AmplienceLinkType {
  openInNewTab: boolean;
  icon?: string;
  label: string;
  url: string;
  tracking: string;
}

export interface TeaserRowItem {
  image: AmplienceImage;
  linkUrl?: AmplienceLinkType | AmplienceLinkType[];
  title: string;
  description?: string;
}

export interface TeaserRowModule extends CmsModule<'https://spreadshirt.com/modules/teaser-row'> {
  anchorId?: string;
  anchorLabel?: string;
  headline?: string;
  slide?: boolean;
  background?: ModuleBackgroundColor;
  items: TeaserRowItem[];
}

export interface HeaderImageModule extends CmsModule<'https://spreadshirt.com/modules/header-image'> {
  anchorId?: string;
  anchorLabel?: string;
  image: AmplienceImage;
  mobileImage?: AmplienceImage;
  cta?: AmplienceLinkType & {
    buttonVariant: ButtonVariant;
  };
  ctas?: { cta: AmplienceLinkType & { buttonVariant: ButtonVariant } }[];
  textVariant: 'white' | 'black';
  headline: string;
  subheadline?: string;
  subheadlineOnTop?: boolean;
}

export interface PromoButtonBase {
  isPromoButton: true;
  label: string;
  icon?: string;
  tracking?: string;
}

interface ButtonVariantHeroTeaser {
  buttonVariant: Extract<ButtonVariant, 'ghost' | 'primary' | 'gradient' | 'gradient-border'>;
}

export interface StandardButtonHeroTeaser extends ButtonVariantHeroTeaser {
  type: 'standardCTA';
  link: AmplienceLinkType;
}

export interface PromoButtonHeroTeaser extends ButtonVariantHeroTeaser {
  type: 'promoCTA';
  promoButton: PromoButtonBase;
}

/**
 * @deprecated Handling of legacy cta schema. Remove after full implementation of DEV-221657 (all modules have been updated and saved in Amplience)
 */
export interface LegacyStandardButtonHeroTeaser {
  cta: AmplienceLinkType & {
    buttonVariant: 'ghost' | 'primary' | 'gradient' | 'gradient-border';
  };
}

export interface HeroTeaserModule extends CmsModule<'https://spreadshirt.com/modules/hero-teaser'> {
  anchorId?: string;
  anchorLabel?: string;
  carouselItem: {
    videoOrImage: {
      formatSelection: 'video' | 'image';
      image?: AmplienceImage;
      mobileImage?: AmplienceImage;
      desktopVideo?: {
        video: AmplienceVideo;
        fallbackImg?: AmplienceImage;
      };
      mobileVideo?: {
        video: AmplienceVideo;
        fallbackImg?: AmplienceImage;
      };
      videoAltText?: string;
      transcriptUrl?: string;
    };
    ctas?: (StandardButtonHeroTeaser | PromoButtonHeroTeaser | LegacyStandardButtonHeroTeaser)[];
    overlay: 'Black' | 'White';
    btnsAlignment: 'horizontal' | 'vertical';
    topLine?: string;
    headline: string;
    subline: boolean;
  }[];
}

interface TeaserCampaignModuleContent {
  anchorId?: string;
  anchorLabel?: string;
  background: ModuleBackgroundColor;
  mobileTextPosition: 'top' | 'bottom';
  videoImagePosition: 'left' | 'right';
  textContent: {
    type: 'condense' | 'normal';
    headline: string;
    description?: string;
  };
  videoOrImage: {
    formatSelection: 'video' | 'image';
    image?: AmplienceImage;
    mobileImage?: AmplienceImage;
    video?: AmplienceVideo;
    fallbackImg?: AmplienceImage[];
    videoAltText?: string;
    transcriptUrl?: string;
  };
  cta: [
    {
      cta: {
        openInNewTab: boolean;
        label: string;
        url: string;
        tracking: string;
        buttonVariant?: 'ghost' | 'primary' | 'gradient' | 'gradient-border';
      };
    },
  ];
}

export interface TeaserCampaignModule
  extends
    CmsModule<'https://spreadshirt.com/modules/teaser-campaign'>,
    TeaserCampaignModuleContent {}

export interface TeaserCampaignMarkdownModule
  extends
    CmsModule<'https://spreadshirt.com/modules/teaser-campaign-markdown'>,
    TeaserCampaignModuleContent {}

export interface videoOrImage {
  formatSelection: 'video' | 'image';
  image?: AmplienceImage;
  mobileImage?: AmplienceImage;
  desktopVideo?: {
    video: AmplienceVideo;
    fallbackImg?: AmplienceImage[];
  };
  mobileVideo?: {
    video: AmplienceVideo;
    fallbackImg?: AmplienceImage[];
  };
}

export interface TeaserCampaignModuleSlider extends CmsModule<'https://spreadshirt.com/modules/teaser-campaign-slider'> {
  anchorId?: string;
  anchorLabel?: string;
  teaserCampaign: TeaserCampaignModule | TeaserCampaignMarkdownModule;
  productSlider: ProductTypeSliderModuleContent;
}

export type ModuleBackgroundColor = 'white' | 'gray' | 'black';
export type CampaignSliderCardVariant = 'default' | 'titleOnImage';

interface CampaignSliderCore {
  heading?: string;
  showAllLink?: AmplienceLinkType | AmplienceLinkType[];
  backgroundColor?: ModuleBackgroundColor;
  anchorId?: string;
  anchorLabel?: string;
}

export interface CampaignSliderProps
  extends CmsModule<'https://spreadshirt.com/modules/campaign-slider'>, CampaignSliderCore {
  items: CampaignCardProps[];
  cardVariant?: CampaignSliderCardVariant;
}

export interface CampaignSliderWithFiltersProps
  extends
    CmsModule<'https://spreadshirt.com/modules/campaign-slider-with-filters'>,
    CampaignSliderCore {
  filters: {
    label: string;
    cardVariant?: CampaignSliderCardVariant;
    items: CampaignCardProps[];
  }[];
}

export interface RecommendationTagsModule extends CmsModule<'https://spreadshirt.com/modules/recommendation-tags'> {
  anchorId?: string;
  anchorLabel?: string;
  headline?: string;
  recommendationTags: AmplienceLinkType[];
}

export interface SEOContentModule extends CmsModule<'https://spreadshirt.com/modules/seo-content'> {
  anchorId?: string;
  anchorLabel?: string;
  headline?: string;
  subHeadline?: string;
  blocks: {
    icon?: string;
    headline: string;
    bodyText: string;
  }[];
}
export interface ProductTypeContentItem {
  productTypeId: number;
  appearanceId?: number;
}

export interface ProductContentItem {
  type: 'mpProduct' | 'productType' | 'blanks' | 'stitchOmat';
  productTypeId?: number;
  appearanceId?: number;
  sellableId?: string;
  compositionId?: string;
  isCustomizable?: boolean;
}

export interface ProductTypeSliderModuleContent extends CmsModule<'https://spreadshirt.com/modules/product-type-slider'> {
  anchorId?: string;
  anchorLabel?: string;
  heading?: string;
  showAllLink?: AmplienceLinkType;
  backgroundColor: 'white' | 'gray';
  items: ProductTypeContentItem[];
}

export interface ProductSliderModuleContent extends CmsModule<'https://spreadshirt.com/modules/product-slider'> {
  anchorId?: string;
  anchorLabel?: string;
  heading?: string;
  showAllLink?: AmplienceLinkType | AmplienceLinkType[];
  backgroundColor: 'white' | 'gray' | 'black';
  items: ProductContentItem[];
}

export interface ReviewsSliderContent extends CmsModule<'https://spreadshirt.com/modules/reviews-slider'> {
  anchorId?: string;
  anchorLabel?: string;
  heading?: string;
}

export interface UspItem {
  icon: string;
  heading: string;
  infoText: string;
}

export interface FooterUspItem extends UspItem {
  moreLink?: AmplienceLinkType;
}

export interface SocialMediaItem {
  link: AmplienceLinkType;
}

export interface FooterLinkItem {
  heading: string;
  links: AmplienceLinkType[];
}
export interface FooterContent {
  content: {
    _meta: HeaderFooterMeta;
    footerUspItems: FooterUspItem[];
    socialMediaIcons: SocialMediaItem[];
    footerLinkItems: FooterLinkItem[];
    newsletter: {
      heading: string;
      subHeading: string;
      placeholder: string;
      button: AmplienceLinkType;
      infoText: string;
      errorMessage: string;
      successHeading: string;
      successText: string;
    };
    countrySwitcherHeading: string;
    image: AmplienceImage;
    seoText: string;
    privacyPolicyLinkList: {
      links: AmplienceLinkType;
    }[];
  };
}

export enum AnnouncementBackgroundColor {
  Black = 'black',
  Red = 'red',
  LightRed = 'lightRed',
  Blue = 'blue',
  LightBlue = 'lightBlue',
  Green = 'green',
  LightGreen = 'lightGreen',
  Pink = 'pink',
  Yellow = 'yellow',
}

export enum AnnouncementTextColor {
  White = 'white',
  Black = 'black',
}

export enum ProductSliderImageBackgroundColor {
  white = 'f4f4f4',
  gray = 'dedede',
  black = 'd6d6d6',
}

export interface TermsAndConditionsModule extends CmsModule<'https://spreadshirt.com/modules/terms-and-conditions'> {
  anchorId?: string;
  anchorLabel?: string;
  headline: string;
  termsAndConditionsList: {
    linkURL: AmplienceLinkType;
    bodyText: string;
  }[];
}

export type MarkdownTextAlignment = 'left' | 'center';

export interface MarkdownElementContent extends CmsModule<'https://spreadshirt.com/modules/markdown-element'> {
  anchorId?: string;
  anchorLabel?: string;
  content: string;
  textAlignment?: MarkdownTextAlignment;
}

export interface RedirectModule {
  content: {
    _meta: HeaderFooterMeta;
    redirects: {
      source: string;
      target: string;
      statusCode: string;
    }[];
  };
}

export interface BreadcrumbsModule extends CmsModule<'https://spreadshirt.com/modules/breadcrumbs'> {
  background?: ModuleBackgroundColor;
}

export interface AboutSpreadGroupModule extends CmsModule<'https://spreadshirt.com/modules/about-spreadgroup'> {
  anchorId?: string;
  anchorLabel?: string;
  headline: string;
  backgroundColor: 'grey' | 'black';
  items: AboutSpreadGroupTeaserItem[];
}

export type SpreadgroupLogo =
  | 'spreadshirt'
  | 'spreadshirtpro'
  | 'spreadlive'
  | 'teamshirts'
  | 'spreadshop'
  | 'spreadconnect'
  | 'spread'
  | 'spreadgroup';

export interface AboutSpreadGroupTeaserItem {
  image: AmplienceImage;
  logo: SpreadgroupLogo;
  imageShape: 'square' | 'circle' | 'roundedSquare';
  cta: AmplienceLinkType;
  paragraph: string;
}

type InPageNavigationModule = CmsModule<'https://spreadshirt.com/modules/in-page-navigation'>;

export interface AboutSpreadshirtModule extends CmsModule<'https://spreadshirt.com/modules/about-spreadshirt'> {
  anchorId?: string;
  anchorLabel?: string;
  background: 'Black' | 'Gray';
  headline: string;
  offerContent: {
    headline: string;
    description: string;
    cta: AmplienceLinkType & {
      buttonVariant: ButtonVariant;
    };
  }[];
  videoOrImage: {
    formatSelection: 'image' | 'video';
    image?: AmplienceImage;
    video?: AmplienceVideo;
    fallbackImg?: AmplienceImage;
    videoAltText?: string;
    transcriptUrl?: string;
  };
}

export interface NewsletterModuleContent extends CmsModule<'https://spreadshirt.com/modules/newsletter-module'> {
  hero?: {
    headline?: string;
    subheadline?: string;
    backgroundImageUrl?: AmplienceImage;
  };
  newsletter: {
    title: string;
    description?: string;
  };
  features?: FeatureItem[];
  privacyPolicy?: {
    privacyPolicyTitle: string;
    privacyPolicyInfo: string;
  };
}

export interface FeatureItem {
  text: string;
  icon?: string;
}

export interface LocalizedField {
  values: {
    locale: Locale;
    value: string;
  }[];
  _meta: {
    schema: string;
  };
}

export interface GenericAnnouncementBarContent {
  title: LocalizedField;
  countdownEnd?: string;
  showCountdown?: boolean;
  ctaLabel?: LocalizedField;
  backgroundColor: AnnouncementBackgroundColor;
  textColor: AnnouncementTextColor;
}

export interface PromoCondition {
  type: 'PercentageBased' | 'CurrencyBased' | 'FreeShipping';
  value: number;
  restrictions: {
    restrictByCategoryOrProductTypes: boolean;
    minOrderValue: number;
    minOrderItems: number;
    productTypeCategories: string[];
    productTypes: string[];
  };
}
export interface PromoToolModule extends CmsModule<'https://spreadshirt.com/modules/promo-tool'> {
  campaignDetails: {
    campaignId: string;
    code: string;
    autoApplyCoupon?: boolean;
    title: LocalizedField;
    description: LocalizedField;
  };
  backgroundImage?: AmplienceImage;
  elements: {
    countdown: boolean;
    headerBanner: boolean;
    strikethroughPrices: boolean;
  };
  announcementBar: GenericAnnouncementBarContent;
  promoConditions: PromoCondition[];
}

export interface ScheduledMeta<T extends string> extends Meta<T> {
  edition?: {
    start: string;
    end: string;
  };
  lifecycle: {
    expiryTime: string;
  };
}

export interface PromoToolSlotContent {
  _meta: ScheduledMeta<'https://spreadshirt.com/slots/promo-tool'>;
  'promo-tool': PromoToolModule;
}

export interface PromoToolSlot {
  content: PromoToolSlotContent;
}

export interface OnPageDiscountModule extends CmsModule<'https://spreadshirt.com/modules/onpage-discount'> {
  anchorId?: string;
  anchorLabel?: string;
}

export interface CustomerAttentionToolContent {
  _meta: ScheduledMeta<'https://spreadshirt.com/slots/customer-attention-slot'>;
  item: CustomerAttentToolItem;
}

export interface CustomerAttentToolItem {
  title: string;
  showBeforePromo: boolean;
  cta: AmplienceLinkType[];
  backgroundColor: AnnouncementBackgroundColor;
  textColor: AnnouncementTextColor;
}

export interface CustomerAttentionToolSlot {
  content: CustomerAttentionToolContent;
}

export type PlpBannerCta =
  | {
      type: 'landing';
      link: AmplienceLinkType;
    }
  | {
      type: 'activateCoupon';
      label: string;
      activatedLabel: string;
      /** Optional icon name from the storefront icon set. Shown before the code is activated. */
      icon?: string;
      /** Optional icon name shown once the code is activated, e.g. `checkcircle`. */
      activatedIcon?: string;
      /**
       * Optional terms-button label. The button opens the promotion pop-up and is omitted when
       * this is empty.
       */
      conditionsLabel?: string;
    };

export interface PlpBannerModule extends CmsModule<'https://spreadshirt.com/modules/plp-banner'> {
  desktopImage: AmplienceImage;
  mobileImage: AmplienceImage;
  headline: string;
  subheadline?: string;
  backgroundColor?: string;
  /**
   * @deprecated Replaced by `cta.conditionsLabel` on an activate-coupon CTA. Still read so banners
   * published before that change keep their terms button until an editor republishes them.
   */
  conditionsLink?: AmplienceLinkType | AmplienceLinkType[];
  cta: PlpBannerCta;
}

export interface PlpBannerSlotContent {
  _meta: ScheduledMeta<'https://spreadshirt.com/slots/plp-banner-slot'>;
  item: PlpBannerModule;
}

export interface PlpBannerSlot {
  content: PlpBannerSlotContent;
}

export interface SeaUspsContent {
  content: {
    _meta: CmsModule<'https://spreadshirt.com/modules/sea-usps'>;
    seaUsps: UspItem[];
  };
}

export type ManagementContentItemBody = {
  _meta: {
    name: string;
    deliveryId: string;
    deliveryKey?: string;
    schema: string;
    hierarchy: {
      parentId: string;
      position: string;
    };
  };
  meta?: {
    robots?: string;
    title?: string;
  };
};

export enum PUBLISHING_STATUS {
  NONE = 'NONE',
  EARLY = 'EARLY',
  LATEST = 'LATEST',
  UNPUBLISHED = 'UNPUBLISHED',
}

export type ManagementContentItem = {
  id: string;
  locale: Locale;
  label: string;
  version: number;
  lastModifiedDate?: string;
  lastPublishedDate?: string;
  publishingStatus: PUBLISHING_STATUS;
  createdDate: string;
  body: ManagementContentItemBody;
};

export type ManagementContentItemResult = {
  _embedded: {
    'content-items': ManagementContentItem[];
  };
  page: {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
  };
};

export interface LicensingListColumnContent {
  title: string;
  links: { linkUrl: AmplienceLinkType }[];
}

export interface LicensingListModuleContent extends CmsModule<'https://spreadshirt.com/modules/licensing-list'> {
  anchorId?: string;
  anchorLabel?: string;
  headline: string;
  column1: LicensingListColumnContent;
  column2: LicensingListColumnContent;
  column3: LicensingListColumnContent;
  column4: LicensingListColumnContent;
}

export interface ProductSliderWithFiltersContent extends CmsModule<'https://spreadshirt.com/modules/product-slider-with-filters'> {
  anchorId?: string;
  anchorLabel?: string;
  heading?: string;
  filters: FilterItem[];
  showAllLink?: AmplienceLinkType | AmplienceLinkType[];
  backgroundColor: 'white' | 'gray' | 'black';
}

interface FilterItem {
  label: string;
  anchorId?: string;
  items: FilteredSliderItem[];
}

export type FilteredSliderItem = ProductContentItem | AdvertisementCardItem;

export interface AdvertisementCardItem {
  type: 'advertisementCard';
  image: AmplienceImage;
  title: string;
  cta: AmplienceLinkType;
}

export interface GiftVoucherContent extends CmsModule<'https://spreadshirt.com/modules/gift-voucher'> {
  anchorId?: string;
  anchorLabel?: string;
  title: string;
  subtitle: string;
  giftVouchers: {
    productTypeId: number;
    title?: string;
    backgroundColor?: string;
  }[];
}

export interface RedemptionRulesContent extends CmsModule<'https://spreadshirt.com/modules/redemption-rules-module'> {
  anchorId?: string;
  anchorLabel?: string;
  headline?: string;
  backgroundColor?: 'white' | 'gray';
  items: {
    icon?: string;
    title?: string;
    bodyText: string;
  }[];
}

export type ChipFilterContent = {
  image: AmplienceImage;
  link: {
    label: string;
    url: string;
  };
};

export interface ChipFilterSliderContent extends CmsModule<'https://spreadshirt.com/modules/chip-filter-slider'> {
  filter: ChipFilterContent[];
}

export interface TShirtOfTheWeekContent {
  _meta: ScheduledMeta<'https://spreadshirt.com/slots/tshirt-of-the-week-slot'>;
  item: TShirtOfTheWeekContentItem;
}

export interface TShirtOfTheWeekContentItem {
  couponCode: string;
  legalText: string;
  items: TShirtOfTheWeekItem[];
  seoTitle: string;
  seoDescription: string;
  heading: string;
}

export interface TShirtOfTheWeekItem {
  compositionId: string;
  sellableId: string;
  appearanceId: string | null;
  buttonLabel: string;
  discountAmount: number;
}

export interface TShirtOfTheWeekSlot {
  content: TShirtOfTheWeekContent;
}

export type TShirtOfTheWeekDiscountModule =
  CmsModule<'https://spreadshirt.com/modules/tshirt-of-the-week-discount'>;

export interface VolumeDiscountProductSliderItem {
  appearanceId: number;
  sellableId: string;
  initialQuantity: number;
  entryName: string;
}

export interface VolumeDiscountCalculatorModuleContent extends CmsModule<'https://spreadshirt.com/modules/volume-discount-calculator'> {
  anchorId: string;
  anchorLabel: string;
  title: string;
  items: VolumeDiscountProductSliderItem[];
  seeAllProductsLink: AmplienceLinkType;
}
